/**
 * Id links (doc 07, screen 6): which of our records each of their ids is.
 * Used by Match, by "Look up a record" steps, and by the client feeds.
 */

import { useState } from "react";
import { Download, Hash, Link2, TriangleAlert, Upload } from "lucide-react";

import { RecordPicker, ENTITY_WORDS } from "@/components/integrations/RecordPicker";
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Column,
  Empty,
  Field,
  FieldRow,
  Panel,
  SearchField,
  Select,
  SkeletonRows,
  Table,
  Td,
  Textarea,
  Th,
  Tile,
  Tiles,
  TonePill,
  Toolbar,
} from "@/components/kit";
import {
  linkId,
  listLinks,
  uploadLinks,
  type IntegrationDetail,
  type LinkEntity,
  type UploadReport,
} from "@/lib/integrations/api";
import { actionWords, linksCsv, readLinksCsv, uploadSummary, type CsvRead } from "@/lib/integrations/csv";
import { when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useDebounced, useKeyed } from "../hooks";

const PAGE = 100;

const ACTION_TONE: Record<string, "good" | "info" | "neutral" | "warning" | "danger"> = {
  add: "good",
  move: "warning",
  same: "neutral",
  not_found: "danger",
  ambiguous: "danger",
  duplicate: "warning",
  blank: "danger",
};

function Uploader({ code, entity, canWrite, onSaved }: { code: string; entity: LinkEntity; canWrite: boolean; onSaved: () => void }) {
  const [text, setText] = useState("");
  const [read, setRead] = useState<CsvRead | null>(null);
  const [report, setReport] = useState<UploadReport | null>(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<{ tone: "good" | "danger"; text: string } | null>(null);

  const check = (content: string) => {
    const parsed = readLinksCsv(content);
    setRead(parsed);
    setReport(null);
    setSaid(null);
    if (parsed.rows.length === 0) return;
    setBusy("check");
    uploadLinks(code, { entity, rows: parsed.rows.map((row) => ({ externalId: row.externalId, code: row.code })), save: false, replace })
      .then(setReport, (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium could not check the file") }))
      .finally(() => setBusy(null));
  };

  const save = () => {
    if (!read) return;
    setBusy("save");
    uploadLinks(code, { entity, rows: read.rows.map((row) => ({ externalId: row.externalId, code: row.code })), save: true, replace })
      .then(
        (done) => {
          setSaid({ tone: "good", text: `Saved ${done.saved.toLocaleString()} ${done.saved === 1 ? "link" : "links"}.` });
          setReport(null);
          setRead(null);
          setText("");
          onSaved();
        },
        (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not save the links") }),
      )
      .finally(() => setBusy(null));
  };

  const counts = report?.counts ?? {};
  const writes = (counts.add ?? 0) + (replace ? (counts.move ?? 0) : 0);

  return (
    <div style={{ display: "grid", gap: "var(--s3)" }}>
      <p style={{ margin: 0, color: "var(--ink-2)" }}>
        A file with two columns: <strong>their id</strong>, <strong>our code</strong> — one {ENTITY_WORDS[entity].one} per line. You see what would change before anything is saved.
      </p>
      <FieldRow>
        <Field label="Pick a file">
          <input
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            disabled={!canWrite}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              void file.text().then((content) => {
                setText(content);
                check(content);
              });
            }}
          />
        </Field>
      </FieldRow>
      <Field label="Or paste it here">
        <Textarea
          rows={4}
          style={{ height: "auto", fontFamily: "ui-monospace, monospace" }}
          value={text}
          disabled={!canWrite}
          placeholder={"their id,our code\nFBL,football"}
          onChange={(event) => setText(event.target.value)}
        />
      </Field>
      <Toolbar>
        <Button disabled={!canWrite || !text.trim() || busy !== null} onClick={() => check(text)}>
          {busy === "check" ? "Checking…" : "Check what would change"}
        </Button>
        <Checkbox
          label="Move ids that already point at another record"
          checked={replace}
          disabled={!canWrite}
          onChange={(event) => {
            setReplace(event.target.checked);
            setReport(null);
          }}
        />
      </Toolbar>
      {read && read.problems.length > 0 && (
        <Banner tone="warning" title="Some lines could not be read:">
          <ul style={{ margin: 0, paddingLeft: "var(--s4)" }}>
            {read.problems.slice(0, 10).map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Banner>
      )}
      {said && <Banner tone={said.tone}>{said.text}</Banner>}
      {report && (
        <>
          <Banner
            tone={writes > 0 ? "info" : "neutral"}
            actions={
              <Button tone="primary" disabled={!canWrite || busy !== null || writes === 0} onClick={save}>
                {busy === "save" ? "Saving…" : `Save ${writes.toLocaleString()} ${writes === 1 ? "link" : "links"}`}
              </Button>
            }
          >
            {uploadSummary(counts, replace)}
          </Banner>
          <div style={{ maxHeight: "24rem", overflow: "auto" }}>
            <Table>
              <thead>
                <tr>
                  <Th num>Line</Th>
                  <Th>Their id</Th>
                  <Th>Our code</Th>
                  <Th>Our record</Th>
                  <Th>What happens</Th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row) => (
                  <tr key={`${row.line}-${row.externalId}`}>
                    <Td num>{row.line}</Td>
                    <Td style={{ fontFamily: "ui-monospace, monospace" }}>{row.externalId || "—"}</Td>
                    <Td style={{ fontFamily: "ui-monospace, monospace" }}>{row.code || "—"}</Td>
                    <Td>
                      {row.name ?? "—"}
                      {row.current && <div style={{ color: "var(--warn)", fontSize: "var(--t-sm)" }}>Now points at {row.current}</div>}
                    </Td>
                    <Td>
                      <TonePill tone={row.action === "move" && !replace ? "neutral" : (ACTION_TONE[row.action] ?? "neutral")}>
                        {row.action === "move" && !replace ? "Left alone: points elsewhere" : actionWords(row.action)}
                      </TonePill>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export function LinksTab({ item, canWrite }: { item: IntegrationDetail; canWrite: boolean }) {
  const timeZone = item.eventTimezone ?? "UTC";
  const [entity, setEntity] = useState<LinkEntity>(item.importCommand.endsWith("entries") ? "participant" : "sport");
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 350);
  const [offset, setOffset] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<{ tone: "good" | "danger"; text: string } | null>(null);
  const links = useKeyed(`links|${item.code}|${entity}|${q}|${offset}`, () => listLinks(item.code, { entity, q, limit: PAGE, offset }));
  const page = links.data;

  const download = async () => {
    setBusy("download");
    try {
      const rows: { externalId: string; code: string | null }[] = [];
      for (let start = 0; ; start += 1000) {
        const part = await listLinks(item.code, { entity, q, limit: 1000, offset: start });
        rows.push(...part.rows.map((row) => ({ externalId: row.externalId, code: row.code })));
        if (part.rows.length < 1000 || rows.length >= part.total) break;
      }
      const blob = new Blob([linksCsv(rows)], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${page?.source ?? item.code}-${entity}-links.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (thrown) {
      setSaid({ tone: "danger", text: errorText(thrown, "The file could not be made") });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Column>
      {page && (
        <Tiles>
          <Tile
            label="Linked ids"
            icon={<Link2 size={14} aria-hidden />}
            value={page.total.toLocaleString()}
            note={`${ENTITY_WORDS[entity].many} linked${q ? " that match the search" : ""}`}
          />
          <Tile
            label="Missing"
            icon={<TriangleAlert size={14} aria-hidden />}
            tone={page.missing.length ? "warning" : "good"}
            value={page.missing.length.toLocaleString()}
            note={page.missing.length ? "Their ids a run met with no record" : "Every id a run met is linked"}
          />
          <Tile label="Source" icon={<Hash size={14} aria-hidden />} value={page.source} note="Whose ids these are" />
        </Tiles>
      )}
      <Toolbar>
        <Select
          aria-label="What kind of record"
          style={{ width: "auto", minWidth: "12rem" }}
          value={entity}
          onChange={(event) => {
            setEntity(event.target.value as LinkEntity);
            setOffset(0);
          }}
        >
          {(Object.keys(ENTITY_WORDS) as LinkEntity[]).map((one) => (
            <option key={one} value={one}>
              What: {ENTITY_WORDS[one].many}
            </option>
          ))}
        </Select>
        <SearchField
          value={search}
          placeholder="Search their id, our code or name"
          aria-label="Search id links"
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />
        <span style={{ flex: 1 }} />
        <Button disabled={!canWrite} onClick={() => setUploading(!uploading)} aria-expanded={uploading}>
          <Upload size={15} aria-hidden />
          Upload a file
        </Button>
        <Button disabled={busy !== null || !page || page.total === 0} onClick={() => void download()}>
          <Download size={15} aria-hidden />
          {busy === "download" ? "Making the file…" : "Download"}
        </Button>
      </Toolbar>
      {said && <Banner tone={said.tone}>{said.text}</Banner>}
      {uploading && (
        <Panel title="Upload id links" icon={<Upload size={18} aria-hidden />}>
          <Uploader code={item.code} entity={entity} canWrite={canWrite} onSaved={links.reload} />
        </Panel>
      )}

      {page && page.missing.length > 0 && (
        <Panel
          title="Missing"
          icon={<TriangleAlert size={18} aria-hidden />}
          note="Their ids a run met that no record is linked to. Rows with them could not be read."
        >
          <Table>
            <thead>
              <tr>
                <Th>Their id</Th>
                <Th num>Rows</Th>
                <Th>Our record</Th>
              </tr>
            </thead>
            <tbody>
              {page.missing.map((one) => (
                <tr key={one.externalId}>
                  <Td style={{ fontFamily: "ui-monospace, monospace" }}>{one.externalId}</Td>
                  <Td num>{one.rows.toLocaleString()}</Td>
                  <Td>
                    <RecordPicker
                      code={item.code}
                      entity={entity}
                      lockEntity
                      disabled={!canWrite}
                      busy={busy === one.externalId}
                      onPick={(hit) => {
                        setBusy(one.externalId);
                        linkId(item.code, { entity, externalId: one.externalId, recordId: hit.id })
                          .then(
                            () => {
                              setSaid({ tone: "good", text: `Linked ${one.externalId} to ${hit.name ?? hit.code}.` });
                              links.reload();
                            },
                            (thrown) => setSaid({ tone: "danger", text: errorText(thrown, "omnium did not save the link") }),
                          )
                          .finally(() => setBusy(null));
                      }}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      )}

      {links.error && <Banner tone="danger" actions={<Button onClick={links.reload}>Try again</Button>}>{links.error}</Banner>}
      {!page ? (
        links.error ? null : <SkeletonRows rows={6} columns={4} />
      ) : page.rows.length === 0 ? (
        <Card>
          <Empty
            title={q ? "No link matches that" : `No ${ENTITY_WORDS[entity].many.toLowerCase()} are linked for ${page.source}`}
            note="Upload a file of their ids and our codes, or pick records for missing ids when a run meets them."
          />
        </Card>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Their id</Th>
                <Th>Our record</Th>
                <Th>Added by</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody style={links.busy ? { opacity: 0.6 } : undefined}>
              {page.rows.map((row) => (
                <tr key={row.externalId}>
                  <Td style={{ fontFamily: "ui-monospace, monospace", overflowWrap: "anywhere" }}>{row.externalId}</Td>
                  <Td>
                    {row.name ?? <span style={{ color: "var(--bad)" }}>The record is gone</span>}
                    {row.code && <span style={{ color: "var(--ink-3)", fontSize: "var(--t-sm)" }}> · {row.code}</span>}
                  </Td>
                  <Td quiet>{[row.origin, row.createdBy].filter(Boolean).join(" · ") || "not recorded"}</Td>
                  <Td quiet>{when(row.createdAt, timeZone)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Toolbar>
            <span style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
              {(offset + 1).toLocaleString()}–{Math.min(page.total, offset + PAGE).toLocaleString()} of {page.total.toLocaleString()}
            </span>
            <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Earlier
            </Button>
            <Button disabled={offset + PAGE >= page.total} onClick={() => setOffset(offset + PAGE)}>
              Later
            </Button>
          </Toolbar>
        </>
      )}
    </Column>
  );
}
